import {
  Box,
  Button,
  Grid,
  GridColumn,
  Icon,
  SidePanel,
  Text
} from "@modernatx-internal/component-library";
import React from "react";

import { ContentComponentProps, PageHeaderContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";

import {
  PageHeaderSubnavDesktop,
  PageHeaderSubnavMobile,
  PageHeaderSubnavProvider,
  usePageHeaderSubnav
} from "./PageHeaderSubnav";
import { Sticky } from "./Sticky";

export const PageHeaderScrollableContext = React.createContext<{
  scrollTo: (element: HTMLElement) => void;
}>({
  scrollTo: () => {}
});

export const PageHeaderArrow: React.FC<{
  direction: "start" | "end";
  onClick: React.MouseEventHandler;
  visible: boolean;
}> = ({ direction, onClick, visible }) => {
  const { screenshot } = useExperience();
  const starting = direction === "start";

  return (
    <Box
      aria-hidden={true}
      data-testid="page-header-arrow"
      sx={{
        alignItems: "center",
        background: `linear-gradient(to ${starting ? "left" : "right"}, transparent 0%, white 50%, white 100%)`,
        cursor: "pointer",
        display: "flex",
        height: "100%",
        justifyContent: "start",
        left: starting ? 0 : null,
        opacity: visible ? 1 : 0,
        paddingInlineStart: 3,
        pointerEvents: visible ? null : "none",
        position: "absolute",
        right: starting ? null : 0,
        mt: "-1px",
        top: 0,
        transition: screenshot ? "none" : "transition-color-medium",
        width: 11
      }}
      onClick={onClick}
    >
      <Icon
        color="button-onSurface-subtle-enabled"
        icon={starting ? "caret-left" : "caret-right"}
        size="md"
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};

export const PageHeaderScrollable: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [arrowStartVisible, arrowStartVisibleSet] = React.useState(false);
  const [arrowEndVisible, arrowEndVisibleSet] = React.useState(false);
  const element = React.useRef<HTMLDivElement>(null);

  const handlePageHeaderArrowEndClick = React.useCallback(() => {
    const el = element.current;

    if (el) {
      el.scrollBy({
        // We need to calculate the maximum for Safari on iOS or else it will overscroll
        left: Math.min(el.clientWidth, el.scrollWidth - el.clientWidth - el.scrollLeft),
        behavior: "smooth"
      });
    }
  }, []);

  const handlePageHeaderArrowStartClick = React.useCallback(() => {
    const el = element.current;

    if (el) {
      el.scrollBy({
        // We need to calculate the maximum for Safari on iOS or else it will overscroll
        left: Math.max(-el.scrollLeft, -el.clientWidth),
        behavior: "smooth"
      });
    }
  }, []);

  const handleScroll = React.useCallback(() => {
    const el = element.current;

    if (el) {
      if (el.scrollWidth > el.clientWidth) {
        if (el.scrollLeft > 1 && !arrowStartVisible) {
          arrowStartVisibleSet(true);
        } else if (el.scrollLeft <= 1 && arrowStartVisible) {
          arrowStartVisibleSet(false);
        }

        const remainingScroll = el.scrollWidth - el.clientWidth - el.scrollLeft;

        if (remainingScroll > 1 && !arrowEndVisible) {
          arrowEndVisibleSet(true);
        } else if (remainingScroll <= 1 && arrowEndVisible) {
          arrowEndVisibleSet(false);
        }
      } else {
        if (arrowStartVisible) {
          arrowStartVisibleSet(false);
        }

        if (arrowEndVisible) {
          arrowEndVisibleSet(false);
        }
      }
    }
  }, [arrowEndVisible, arrowStartVisible]);

  const handleScrollTo = React.useCallback((target: HTMLElement) => {
    const el = element.current;

    if (el) {
      const offsetLeft =
        target.offsetLeft - el.offsetLeft - el.clientWidth / 2 + target.clientWidth / 2;

      el.scrollTo({
        left: offsetLeft,
        behavior: "smooth"
      });
    }
  }, []);

  React.useEffect(() => {
    globalThis.addEventListener("resize", handleScroll);

    return () => {
      globalThis.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  React.useEffect(() => {
    handleScroll();
  });

  return (
    <PageHeaderScrollableContext.Provider value={{ scrollTo: handleScrollTo }}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <Box
          ref={element}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            position: "relative",
            scrollbarWidth: "none",
            width: "100%",
            // @ts-ignore
            "::-webkit-scrollbar": {
              display: "none"
            }
          }}
          onScroll={handleScroll}
        >
          {children}
        </Box>
        <PageHeaderArrow
          direction="start"
          visible={arrowStartVisible}
          onClick={handlePageHeaderArrowStartClick}
        />
        <PageHeaderArrow
          direction="end"
          visible={arrowEndVisible}
          onClick={handlePageHeaderArrowEndClick}
        />
      </Box>
    </PageHeaderScrollableContext.Provider>
  );
};

const PageHeaderWithoutSubnavProvider: React.FC<ContentComponentProps<PageHeaderContent>> = ({
  button,
  children,
  hamburger,
  localeSelector,
  logo,
  sidePanelLinks,
  sticky,
  variant
}) => {
  const [sidePanelVisible, sidePanelVisibleSet] = React.useState(false);
  const { setPageHeaderSubnav } = usePageHeaderSubnav();
  const sidePanelAvailable = !!localeSelector || !!sidePanelLinks?.length;

  const handleMouseLeave = React.useCallback(() => {
    setPageHeaderSubnav({
      links: null,
      target: null
    });
  }, [setPageHeaderSubnav]);

  const desktopHeaderContent = (
    <>
      {logo ? (
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            justifyContent: "center",
            py: 3
          }}
        >
          {logo}
        </Box>
      ) : (
        <Box />
      )}
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: [0, null, 2],
          marginInlineStart: "auto"
        }}
      >
        {!!React.Children.count(children) && (
          <Box as="nav" sx={{ display: ["none", null, "block"] }}>
            <Box
              sx={{
                display: "inline-flex",
                paddingInlineStart: 5
              }}
            >
              {children}
            </Box>
          </Box>
        )}
        <Box sx={{ flexShrink: 0 }}>{button}</Box>
        {sidePanelAvailable && (
          <Box sx={{ display: ["flex", null, "none"], flexShrink: 0 }}>
            <Button
              aria-label="Open side panel"
              icon="list"
              size="lg"
              variant="subtle"
              onClick={() => sidePanelVisibleSet(true)}
            />
          </Box>
        )}
      </Box>
    </>
  );

  return (
    <>
      <Sticky
        as="header"
        role="banner"
        disabled={sticky === false}
        peekaboo={true}
        sx={{
          backgroundColor: "surface-neutral",
          borderBottomStyle: "solid",
          borderBottomWidth: sticky === false ? 0 : "1px",
          borderColor: "stroke-neutral-secondary"
        }}
        onMouseLeave={handleMouseLeave}
      >
        {variant === "uniform-12" ? (
          <Grid variant="uniform-12">
            <GridColumn
              sx={{
                alignItems: "center",
                flexDirection: "row",
                mb: "-1px",
                minHeight: 18
              }}
            >
              {desktopHeaderContent}
            </GridColumn>
          </Grid>
        ) : (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              mb: "-1px",
              minHeight: 18,
              px: [5, null, 8]
            }}
          >
            {desktopHeaderContent}
          </Box>
        )}
        <PageHeaderSubnavDesktop />
      </Sticky>
      {!!React.Children.count(children) && !hamburger && (
        <>
          <Sticky
            as="header"
            sx={{
              backgroundColor: "surface-neutral",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              borderColor: "stroke-neutral-secondary",
              display: ["flex", null, "none"],
              flexDirection: "column",
              position: "relative"
            }}
          >
            <Box sx={{ display: "flex", pt: 3 }}>
              {variant === "uniform-12" ? (
                <Grid variant="uniform-12">
                  <GridColumn>
                    <PageHeaderScrollable>
                      <Box
                        as="nav"
                        sx={{
                          display: "inline-flex"
                        }}
                      >
                        {children}
                      </Box>
                    </PageHeaderScrollable>
                  </GridColumn>
                </Grid>
              ) : (
                <PageHeaderScrollable>
                  <Box
                    as="nav"
                    sx={{
                      display: "inline-flex",
                      paddingInlineEnd: [4, null, 8],
                      paddingInlineStart: [5, null, 8]
                    }}
                  >
                    {children}
                  </Box>
                </PageHeaderScrollable>
              )}
            </Box>
          </Sticky>
          <PageHeaderSubnavMobile links={children} />
        </>
      )}
      <SidePanel
        visible={sidePanelAvailable && sidePanelVisible}
        onDismiss={() => sidePanelVisibleSet(false)}
      >
        {localeSelector && (
          <Box
            sx={{
              pb: 5,
              px: 3,
              fontSize: "font-size-6"
            }}
          >
            {localeSelector}
          </Box>
        )}
        <Text
          as="div"
          sx={{
            alignItems: "start",
            color: "text-link",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            "& a, & button": {
              fontWeight: "font-weight-bold",
              px: 3,
              py: 2
            },
            "& a, & button, & a *, & button *": {
              color: "text-link"
            }
          }}
          variant="title-1-medium"
        >
          {sidePanelLinks?.map((link, i) => (
            <Box key={i} onClick={() => sidePanelVisibleSet(false)}>
              {link}
            </Box>
          ))}
        </Text>
      </SidePanel>
    </>
  );
};

export const PageHeader: React.FC<ContentComponentProps<PageHeaderContent>> = (props) => (
  <PageHeaderSubnavProvider>
    <PageHeaderWithoutSubnavProvider {...props} />
  </PageHeaderSubnavProvider>
);
